import { TOKEN_IMAGE_KEY, linkKey, type NoteDetail } from '@kartothek/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError } from '../api';
import { t } from '../i18n';
import { Combobox } from './Combobox';
import { imageTarget } from './ImagePicker';

const VIEW = 300;
const OUT = 512;

/** Token-Bild einer Notiz (URL über die aufgelösten Links). */
export function tokenImageUrl(note: NoteDetail): string | null {
  const target = imageTarget(note.frontmatter[TOKEN_IMAGE_KEY]);
  if (!target) return null;
  const link = note.links[linkKey(target)];
  return link?.type === 'file' ? link.url : null;
}

/** Dateiname aus dem Notiztitel (ohne Zeichen, die in Dateinamen stören). */
function safeName(title: string): string {
  return `${title.replace(/[\\/:*?"<>|#^[\]]/g, '_').trim() || 'Token'}.png`;
}

/**
 * Zuschnitt wie bei einem Profilbild: Kreis über dem Bild, ziehen verschiebt, Regler/Mausrad zoomt.
 * Ergebnis: rundes PNG (512 px) in `Tokens/`, eingetragen als `token: "[[Tokens/…]]"`.
 */
function Cropper(props: { cid: string; note: NoteDetail; onClose: () => void }) {
  const qc = useQueryClient();
  const files = useQuery({ queryKey: ['files', props.cid], queryFn: () => api.files(props.cid), staleTime: 30_000 });
  const options = useMemo(
    () => (files.data ?? []).filter((f) => f.mime.startsWith('image/')).map((f) => ({ key: f.id, title: f.name, hint: f.folder, url: f.url })),
    [files.data],
  );
  const [pick, setPick] = useState('');
  const [src, setSrc] = useState<string | null>(null);
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [center, setCenter] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const drag = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!src) return;
    const el = new Image();
    el.onload = () => {
      setImg(el);
      setZoom(1);
      setCenter({ x: el.width / 2, y: el.height / 2 });
    };
    el.onerror = () => setError(t('token.loadError'));
    el.src = src;
  }, [src]);

  const scale = img ? (VIEW / Math.min(img.width, img.height)) * zoom : 1;

  // Ausschnitt zeichnen: Bild + abgedunkelter Bereich außerhalb des Kreises
  useEffect(() => {
    const cv = canvasRef.current;
    const ctx = cv?.getContext('2d');
    if (!cv || !ctx) return;
    ctx.clearRect(0, 0, VIEW, VIEW);
    if (!img) return;
    ctx.drawImage(img, VIEW / 2 - center.x * scale, VIEW / 2 - center.y * scale, img.width * scale, img.height * scale);
    ctx.fillStyle = 'rgba(10, 6, 3, 0.65)';
    ctx.beginPath();
    ctx.rect(0, 0, VIEW, VIEW);
    ctx.arc(VIEW / 2, VIEW / 2, VIEW / 2 - 4, 0, Math.PI * 2, true);
    ctx.fill('evenodd');
    ctx.strokeStyle = '#f0c46c';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(VIEW / 2, VIEW / 2, VIEW / 2 - 4, 0, Math.PI * 2);
    ctx.stroke();
  }, [img, center, scale]);

  const save = async () => {
    if (!img) return;
    setBusy(true);
    setError(null);
    try {
      const out = document.createElement('canvas');
      out.width = OUT;
      out.height = OUT;
      const ctx = out.getContext('2d')!;
      const k = OUT / VIEW;
      ctx.beginPath();
      ctx.arc(OUT / 2, OUT / 2, OUT / 2, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(img, OUT / 2 - center.x * scale * k, OUT / 2 - center.y * scale * k, img.width * scale * k, img.height * scale * k);
      const blob = await new Promise<Blob | null>((resolve) => out.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error(t('token.loadError'));
      const res = await api.uploadFile(props.cid, new File([blob], safeName(props.note.title), { type: 'image/png' }), 'Tokens');
      const note = await api.patchFrontmatter(props.cid, props.note.id, { [TOKEN_IMAGE_KEY]: `[[${res.folder ? `${res.folder}/` : ''}${res.name}]]` });
      qc.setQueryData(['note', props.cid, props.note.id], note);
      void qc.invalidateQueries({ queryKey: ['files', props.cid] });
      void qc.invalidateQueries({ queryKey: ['map', props.cid] });
      props.onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onMouseDown={props.onClose}>
      <div className="modal token-cropper" onMouseDown={(e) => e.stopPropagation()}>
        <h2>{t('token.title', { name: props.note.title })}</h2>
        <div className="row">
          <button onClick={() => fileRef.current?.click()}>⇪ {t('token.upload')}</button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) setSrc(URL.createObjectURL(f));
              e.target.value = '';
            }}
          />
          <Combobox
            value={pick}
            onChange={setPick}
            options={options}
            placeholder={t('token.pickExisting')}
            onPick={(o) => {
              setPick(o.title);
              setSrc(o.url);
            }}
          />
        </div>
        <canvas
          ref={canvasRef}
          width={VIEW}
          height={VIEW}
          className="token-crop-canvas"
          onPointerDown={(e) => {
            drag.current = { x: e.clientX, y: e.clientY };
            (e.target as HTMLElement).setPointerCapture(e.pointerId);
          }}
          onPointerMove={(e) => {
            if (!drag.current || !img) return;
            const dx = e.clientX - drag.current.x;
            const dy = e.clientY - drag.current.y;
            drag.current = { x: e.clientX, y: e.clientY };
            setCenter((c) => ({ x: c.x - dx / scale, y: c.y - dy / scale }));
          }}
          onPointerUp={() => (drag.current = null)}
          onWheel={(e) => setZoom((z) => Math.min(8, Math.max(0.3, z * (e.deltaY > 0 ? 1 / 1.1 : 1.1))))}
        />
        {img ? (
          <label className="sb-field">
            <span>{t('token.zoom')}</span>
            <input type="range" min={0.3} max={8} step={0.05} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} />
          </label>
        ) : (
          <p className="small muted">{t('token.hint')}</p>
        )}
        {error && <p className="error small">{error}</p>}
        <div className="modal-actions">
          <button onClick={props.onClose}>{t('common.cancel')}</button>
          <button className="primary" disabled={!img || busy} onClick={() => void save()}>
            {busy ? t('common.saving') : t('token.apply')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Token-Bild einer Kreatur in der Leseansicht: Vorschau, Zuschnitt, Entfernen. */
export function TokenImageControl({ note }: { note: NoteDetail }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const url = tokenImageUrl(note);
  return (
    <div className="token-image-control">
      <span className="token-avatar">{url ? <img src={url} alt="" /> : <span className="muted">?</span>}</span>
      <button className="small" onClick={() => setOpen(true)}>
        {url ? t('token.change') : t('token.set')}
      </button>
      {url && (
        <button
          className="small"
          onClick={() =>
            void api.patchFrontmatter(note.campaignId, note.id, {}, [TOKEN_IMAGE_KEY]).then((res) => {
              qc.setQueryData(['note', note.campaignId, note.id], res);
              void qc.invalidateQueries({ queryKey: ['map', note.campaignId] });
            })
          }
        >
          {t('token.remove')}
        </button>
      )}
      {open && <Cropper cid={note.campaignId} note={note} onClose={() => setOpen(false)} />}
    </div>
  );
}
